import React, { useState } from 'react';
import { useProfile } from '../../context/ProfileContext';
import { useNavigate } from 'react-router-dom';
import { getAuth, signOut } from "firebase/auth";
import AnimatedPageWrapper from '../../components/miscellaneous/AnimatedPageWrapper';
import toast from "react-hot-toast";
import { getFunctions, httpsCallable } from "firebase/functions";
import ComingSoonModal from '../../components/miscellaneous/ComingSoonModal';

// Sub-components (Tabs)
import AccountsTab from './tabs/AccountsTab';
import AchievementsTab from './tabs/AchievementsTab';
import PaymentDetailsTab from './tabs/PaymentDetailsTab';
import AppearanceTab from './tabs/AppearanceTab';

const SettingsPage = () => {
  const { 
    setActiveProfile, 
    setUserProfile, 
    activeProfile, 
    linkedArtistAccounts = [], 
    refreshContext 
  } = useProfile();
  
  const [activeTab, setActiveTab] = useState('menu');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [artistToDelete, setArtistToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isComingSoonModalVisible, setIsComingSoonModalVisible] = useState(false);
  
  const navigate = useNavigate();
  const auth = getAuth();

  const menuItems = [
    { 
      id: 'accounts', 
      label: 'Accounts & Profiles', 
      badge: linkedArtistAccounts.length > 0 ? linkedArtistAccounts.length : null 
    },
    { id: 'appearance', label: 'Appearance' },
    { id: 'achievements', label: 'Achievements' },
    { id: 'payment_details', label: 'Payment & Subscription' },
  ];

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem("activeProfile");
      if (setActiveProfile) setActiveProfile(null);
      if (setUserProfile) setUserProfile(null);

      toast.success("Signed out successfully");
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
      toast.error("Failed to sign out. Please try again.");
    }
  };

  const handleOpenDeleteArtistModal = (artist) => {
    setArtistToDelete(artist);
    setShowDeleteConfirm(true);
  };

  const confirmArtistDeletion = async () => {
    const targetId = artistToDelete?.id || (activeProfile?.type === 'artist' ? activeProfile.id : null);
    if (!targetId) return;

    setIsDeleting(true);
    const functions = getFunctions(undefined, "us-central1");
    const deleteArtistAccount = httpsCallable(functions, "deleteArtistAccount");

    try {
      await deleteArtistAccount({ artistId: targetId });
      toast.success("Artist account flagged for deletion.");
      
      await refreshContext();
      setShowDeleteConfirm(false);
      setArtistToDelete(null);
      navigate("/");
    } catch (error) {
      console.error("Deletion error:", error);
      toast.error("Failed to delete artist account.");
    } finally {
      setIsDeleting(false);
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'accounts': 
        return <AccountsTab onOpenDeleteArtist={handleOpenDeleteArtistModal} />;
      case 'appearance': 
        return <AppearanceTab />;
      case 'achievements': 
        return <AchievementsTab />;
      case 'payment_details': 
        return <PaymentDetailsTab />;
      default: 
        return (
          <div className="space-y-3 animate-in fade-in duration-300">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className="w-full flex items-center justify-between p-5 bg-white dark:bg-brand-gray/20 border border-slate-100 dark:border-brand-gray rounded-2xl shadow-sm hover:scale-[1.01] transition-all active:scale-[0.98]"
              >
                <div className="flex items-center space-x-3">
                  <span className="font-bold text-slate-800 dark:text-brand-white tracking-tight">
                    {item.label}
                  </span>
                  {item.badge !== null && (
                    <span className="text-xs font-bold px-2 py-0.5 bg-brand-pink/10 text-brand-pink rounded-full">
                      {item.badge}
                    </span>
                  )}
                </div>
                <span className="text-slate-300 dark:text-brand-gray">❯</span>
              </button>
            ))}
          </div>
        );
    }
  };

  return (
    <AnimatedPageWrapper direction="right">
      <div className="min-h-screen bg-slate-50 dark:bg-brand-black p-4 pt-12 pb-32 transition-colors duration-500">
        <div className="max-w-xl mx-auto">
          
          <div className="flex items-center mb-8">
            {activeTab !== 'menu' && (
              <button 
                onClick={() => setActiveTab('menu')}
                className="mr-4 p-2 bg-white dark:bg-brand-gray/40 rounded-full shadow-sm text-slate-900 dark:text-brand-white"
              >
                ❮
              </button>
            )}
            <h1 className="text-3xl font-black tracking-tighter dark:text-brand-white uppercase">
              {activeTab === 'menu' ? 'Settings' : 'Back'}
            </h1>
          </div>

          {renderContent()}

          {/* GLOBAL ACTIONS */}
          {activeTab === 'menu' && (
            <div className="space-y-4 mt-10">
              <button 
                onClick={handleSignOut}
                className="w-full p-5 text-brand-pink font-black uppercase tracking-widest text-sm bg-transparent border-2 border-brand-pink/30 rounded-2xl transition-all active:scale-95 active:bg-brand-pink/10 hover:border-brand-pink/60 hover:shadow-[0_0_15px_rgba(255,0,204,0.1)]"
              >
                Sign Out of All Accounts
              </button>

              <button 
                onClick={() => setIsComingSoonModalVisible(true)}
                className="w-full p-4 text-red-500/70 text-xs font-bold uppercase tracking-wider bg-transparent transition-all hover:text-red-500"
              >
                Delete Listener Account
              </button>
            </div>
          )}
        </div>
      </div>

      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-brand-black dark:border dark:border-brand-gray rounded-xl p-6 max-w-sm w-full shadow-2xl text-center">
            <h3 className="text-xl font-bold text-gray-900 dark:text-brand-white">Delete Artist Account?</h3>
            <p className="text-sm text-gray-500 dark:text-brand-gray mt-2">
              Are you sure you want to delete <span className="font-bold text-slate-800 dark:text-brand-white">{artistToDelete?.artistName || activeProfile?.artistName}</span>? 
              Music and profile data will be hidden for 90 days before being permanently purged.
            </p>
            <div className="flex gap-3 mt-6">
              <button 
                disabled={isDeleting}
                onClick={() => { setShowDeleteConfirm(false); setArtistToDelete(null); }}
                className="flex-1 px-4 py-2 text-sm font-bold text-gray-500 dark:text-brand-gray hover:bg-gray-100 dark:hover:bg-brand-gray/20 rounded-lg transition-all"
              >
                Cancel
              </button>
              <button 
                disabled={isDeleting}
                onClick={confirmArtistDeletion}
                className="flex-1 px-4 py-2 text-sm font-bold bg-red-600 text-white rounded-lg hover:bg-red-700 shadow-md transition-all"
              >
                {isDeleting ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ComingSoonModal 
        isOpen={isComingSoonModalVisible}
        onClose={() => setIsComingSoonModalVisible(false)}
      />
    </AnimatedPageWrapper>
  );
};

export default SettingsPage;